import type { LensCardData, LensResolvedPeriod } from '~/types/leLens';

export const LENS_DATA_ENCODING = 'columnar-v1' as const;

export type LensColumnarTable = {
  columns: Array<string>;
  data: Array<Array<unknown>>;
};

export type LensCompactCard = Omit<LensCardData, 'rows' | 'columns'> & {
  result?: string;
};

export type LensCompactSnapshot = {
  encoding: typeof LENS_DATA_ENCODING;
  datasets: Record<string, LensColumnarTable>;
  cards: Array<LensCompactCard>;
  filterOptions?: Record<string, Array<string>>;
  period?: LensResolvedPeriod;
};

export type LensCompactCardResponse = {
  encoding: typeof LENS_DATA_ENCODING;
  datasets: Record<string, LensColumnarTable>;
  card: LensCompactCard;
  cached?: boolean;
};

function decodeTable(table: LensColumnarTable): Array<Record<string, unknown>> {
  return table.data.map((values) => Object.fromEntries(table.columns.map((column, index) => [column, values[index]])));
}

function expandCard(card: LensCompactCard, datasets: Record<string, LensColumnarTable>): LensCardData {
  const { result, ...rest } = card;
  if (!result || !datasets[result]) {
    return rest;
  }
  const table = datasets[result];
  return { ...rest, columns: table.columns, rows: decodeTable(table) };
}

export function expandLensDataResponse(body: {
  encoding?: string;
  datasets?: Record<string, LensColumnarTable>;
  cards?: Array<LensCompactCard | LensCardData>;
  filterOptions?: Record<string, Array<string>>;
  period?: LensResolvedPeriod;
  lensVersion?: number;
  viewRevision?: number;
}): {
  cards: Array<LensCardData>;
  filterOptions: Record<string, Array<string>>;
  period?: LensResolvedPeriod;
  lensVersion?: number;
  viewRevision?: number;
} {
  const filterOptions = body.filterOptions ?? {};
  const period = body.period;
  const lensVersion = body.lensVersion;
  const viewRevision = body.viewRevision;
  if (body.encoding !== LENS_DATA_ENCODING) {
    return {
      cards: (body.cards ?? []) as Array<LensCardData>,
      filterOptions,
      ...(period ? { period } : {}),
      ...(lensVersion !== undefined ? { lensVersion } : {}),
      ...(viewRevision !== undefined ? { viewRevision } : {}),
    };
  }
  const decoded: Record<string, { columns: Array<string>; rows: Array<Record<string, unknown>> }> = {};
  for (const [id, table] of Object.entries(body.datasets ?? {})) {
    decoded[id] = { columns: table.columns, rows: decodeTable(table) };
  }
  return {
    filterOptions,
    ...(period ? { period } : {}),
    ...(lensVersion !== undefined ? { lensVersion } : {}),
    ...(viewRevision !== undefined ? { viewRevision } : {}),
    cards: (body.cards ?? []).map((card) => {
      const compact = card as LensCompactCard;
      const { result, ...rest } = compact;
      if (!result || !decoded[result]) {
        return rest;
      }
      return { ...rest, ...decoded[result] };
    }),
  };
}

export function expandLensCardResponse(body: {
  encoding?: string;
  datasets?: Record<string, LensColumnarTable>;
  card?: LensCompactCard | LensCardData;
  cached?: boolean;
}): { card: LensCardData; cached?: boolean } {
  if (!body.card) {
    return { card: { cardId: '', status: 'error', message: 'No card in the response.' } };
  }
  if (body.encoding !== LENS_DATA_ENCODING) {
    return { card: body.card as LensCardData, cached: body.cached };
  }
  return { card: expandCard(body.card, body.datasets ?? {}), cached: body.cached };
}
